// src/services/messaging/handlers/filesUpdatedHandler.ts

import type { FilesUpdatedResult } from "@/services/abstract/poc/messaging.service.interface";
import type { HeliaNode } from "@/types/poc/helia";
import type { LibP2pWithServices } from "@/types/poc/libp2p";
import type { MessagePayload, MessageHandler } from "@/types/poc/message";
import { createLogger, type Logger } from "@/utils/logger";

const log: Logger = createLogger({ namespace: 'six3ro:p2p:handlers:files-updated' });

/**
 * Creates a pure files-updated message handler.
 * This handler processes files-updated messages without side effects.
 */
export const createFilesUpdatedHandler = (): MessageHandler<FilesUpdatedResult> => ({
  canHandle: (topic: string, message: MessagePayload): boolean =>
    message.type === 'files-updated',

  process: (
    message: MessagePayload,
    libp2p: LibP2pWithServices,
    heliaNode: HeliaNode | null
  ): FilesUpdatedResult => {
    if (message.type !== 'files-updated') {
      throw new Error(`Invalid message type: expected 'files-updated', got '${message.type}'`);
    }

    const { files, sender } = message;
    log.info('Received files-updated with', files.length, 'files from', sender);

    // REMOVE ALL BUSINESS LOGIC - just validate and return
    // NO file existence checks, NO shouldReplicate logic
    const validatedFiles = files
      .filter((file) => (file.cid.length !== 0))
      .map((file) => ({
        cid: file.cid,
        metadata: file.metadata,
        shouldReplicate: false, // Will be determined in Redux
        messageTimestamp: message.timestamp
      }));

    return {
      sender,
      files: validatedFiles,
    };
  }
});
